import eventSchedule from "@/data/event-schedule.js";

//
// Sort an array of meals by by restaurant rank, amount and start time
//
export function useEventByDate(startDate = null, endDate = null) {
  if (!startDate && !endDate) return;
  return eventSchedule.find(
    (item) => item.startDate === startDate && item.endDate === endDate
  );
}

//
// Sort an array of meals by by restaurant rank, amount and start time
//
export function useOrderMealsByRank(meals) {
  return meals?.sort((a, b) => {
    // First, sort by restaurant rank in descending order
    const rankDifference = b.restaurant?.rank - a.restaurant?.rank;
    const minAmountDifference = a.minAmount - b.minAmount;

    if (rankDifference !== 0) {
      return rankDifference;
    }

    if (minAmountDifference !== 0) {
      return minAmountDifference;
    }

    // If amount is the same, sort by start time in descending order
    const timeA = a.startTime.split(":").map(Number);
    const timeB = b.startTime.split(":").map(Number);
    const minutesA = timeA[0] * 60 + timeA[1];
    const minutesB = timeB[0] * 60 + timeB[1];

    return minutesB - minutesA;
  });
}

//
// Filter an array of meals to prevent max restaurant occurrences
//
export function useFilterMealsByMaxRestaurantOccurrences(
  meals,
  maxOccurrences
) {
  // Create empty array
  const restaurantMeals = new Map();

  meals = meals || [];

  // Group meals by restaurant ID and keep only the first three meals for each restaurant
  meals.forEach((event) => {
    const id = event.restaurant.id;
    if (!restaurantMeals.has(id)) {
      restaurantMeals.set(id, [event]);
    } else {
      const meals = restaurantMeals.get(id);
      if (meals.length < maxOccurrences) {
        meals.push(event);
      }
      restaurantMeals.set(id, meals);
    }
  });

  // Flatten the grouped events into a single array
  return Array.from(restaurantMeals.values()).flat();
}

//
// Group an array of meals based on date (nextDates and startDate)
//
export function useGroupMealsByDate(meals, date = null) {
  // Step 1: Group the meals by date
  const groupedMeals = meals.reduce((acc, meal) => {
    // Get all available next dates
    if (meal.nextDates?.length) {
      meal.nextDates.forEach((nextDate) => {
        if (!date || date === nextDate) {
          if (!acc[nextDate]) {
            acc[nextDate] = [];
          }

          acc[nextDate].push(meal);
        }
      });
    }

    // If no next dates, use start date
    if (!meal.nextDates?.length) {
      if (!date || date === meal.startDate) {
        if (!acc[meal?.startDate]) {
          acc[meal?.startDate] = [];
        }

        acc[meal.startDate].push(meal);
      }
    }

    return acc;
  }, {});

  // Step 2: Extract and sort the keys (start dates) chronologically
  const sortedKeys = Object.keys(groupedMeals).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  // Step 3: Create a new object with the sorted keys
  return sortedKeys.reduce((acc, key) => {
    acc[key] = groupedMeals[key];
    return acc;
  }, {});
}

//
// Function to group meals by week starting on Monday
//
export function useGroupMealsByWeek(meals) {
  const dayjs = useDayjs();

  // Step 1: Group the meals by date
  const groupedMeals = meals.reduce((acc, meal) => {
    if (meal.nextDates?.length) {
      meal.nextDates.forEach((nextDate) => {
        const startDateWeek = dayjs(nextDate)
          .startOf("week")
          .format("YYYY-MM-DD");

        if (!acc[startDateWeek]) {
          acc[startDateWeek] = [];
        }

        acc[startDateWeek].push(meal);
        acc[startDateWeek] = useUniqueBy(acc[startDateWeek], "id");
      });
    }

    if (!meal.nextDates?.length) {
      const startDateWeek = dayjs(meal.startDate)
        .startOf("week")
        .format("YYYY-MM-DD");

      if (!acc[startDateWeek]) {
        acc[startDateWeek] = [];
      }

      acc[startDateWeek].push(meal);
      acc[startDateWeek] = useUniqueBy(acc[startDateWeek], "id");
    }

    return acc;
  }, {});

  // Step 2: Extract and sort the keys (start dates) chronologically
  const sortedKeys = Object.keys(groupedMeals).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  // Step 3: Create a new object with the sorted keys
  return sortedKeys.reduce((acc, key) => {
    acc[key] = groupedMeals[key];
    return acc;
  }, {});
}

//
// Group and filter "events",
// Remove short names/descriptions
// Set max occurrences and group by start date
//
export function useGroupedMeals(meals, maxOccurrences) {
  let filteredMeals = meals?.filter((meal) => meal.name.length > 2);

  filteredMeals = useFilterMealsByMaxRestaurantOccurrences(
    filteredMeals,
    maxOccurrences
  );

  return useGroupMealsByDate(filteredMeals);
}

//
// Filter an array of meals based on min limit and dates
//
export function useFilterMealsForEventsStart(meals, minLimit = 12) {
  const dayjs = useDayjs();

  // Set dates to cap meals on
  const capDates = {
    date: dayjs().format("YYYY-MM-DD"),
    tomorrow: dayjs().add(1, "day").format("YYYY-MM-DD"),
    endOfWeek: dayjs().endOf("week").format("YYYY-MM-DD"),
    endOfMonth: dayjs().endOf("month").format("YYYY-MM-DD"),
    next7Days: dayjs().add(7, "days").format("YYYY-MM-DD"),
    next30Days: dayjs().add(30, "days").format("YYYY-MM-DD"),
  };

  // Cap meals based on min limit
  if (meals?.length > minLimit) {
    // console.log('capDates', capDates)

    for (const key in capDates) {
      // console.log('capDate', capDates[key])
      const capDate = capDates[key];
      const capMeals = meals.filter(
        (meal) => !dayjs(meal.nextDates[0]).isAfter(capDate)
      );

      if (capMeals?.length >= minLimit) {
        return capMeals;
        break;
      }
    }

    return meals.slice(0, minLimit);
  }

  return meals;
}
